import { useEffect, useState } from "react";
import { BlogHeader } from "@/components/blog/BlogHeader";
import { BlogLayout } from "@/components/blog/BlogLayout";
import { BlogFiltersSection } from "@/components/blog/BlogFiltersSection";
import { ServerBlogContentSection } from "@/components/blog/ServerBlogContentSection";
import { useCategoryFetch } from "@/hooks/blog/useCategoryFetch";
import { useRef } from "react";
import { BlogPost } from "@/types/blog";
import { getServerBlogPosts } from "@/hooks/blog/useServerBlogPosts";

/**
 * Versione ottimizzata per il server della pagina Blog
 * Questa versione è progettata per essere utilizzata durante il rendering lato server
 * e garantisce che i post siano disponibili immediatamente per il SEO.
 */

interface ServerBlogPageProps {
  ssrPosts?: BlogPost[];
}

const ServerBlogPage = ({ ssrPosts = [] }: ServerBlogPageProps) => {
  // Ottieni i post precaricati dal contesto globale, dai props SSR o dallo script __BLOG_DATA__ se disponibili
  let globalPosts: BlogPost[] = ssrPosts;
  
  // Solo nel browser, prova a recuperare i dati dallo script o dal contesto globale
  if (typeof window !== 'undefined' && globalPosts.length === 0) {
    // Controlla se esiste lo script __BLOG_DATA__
    const blogDataScript = document.getElementById('__BLOG_DATA__');
    if (blogDataScript) {
      try {
        const blogData = JSON.parse(blogDataScript.textContent || '{"posts":[]}');
        globalPosts = blogData.posts || [];
        console.log(`[ServerBlogPage] Caricati ${globalPosts.length} post dallo script __BLOG_DATA__`);
      } catch (e) {
        console.error('[ServerBlogPage] Errore nel parsing dello script __BLOG_DATA__:', e);
      }
    }
    
    // Fallback al contesto globale se non abbiamo trovato lo script
    if (globalPosts.length === 0 && typeof globalThis !== 'undefined') {
      globalPosts = (globalThis as any).__BLOG_POSTS || [];
      console.log(`[ServerBlogPage] Caricati ${globalPosts.length} post dal contesto globale`);
    }
  }

  // Durante il SSR, usa direttamente i post forniti tramite props o contesto globale
  if (typeof window === 'undefined') {
    // Prova a ottenere i post dal contesto globale durante il SSR
    if (globalPosts.length === 0 && typeof globalThis !== 'undefined') {
      globalPosts = (globalThis as any).__BLOG_POSTS || [];
      console.log(`[ServerBlogPage SSR] Caricati ${globalPosts.length} post dal contesto globale`);
    }
  }

  // Stato per i post del blog - inizializzato con i post precaricati
  const [posts, setPosts] = useState<BlogPost[]>(globalPosts);
  const [loading, setLoading] = useState(false);
  const [totalPostsLoaded, setTotalPostsLoaded] = useState(globalPosts.length);

  // Recupera categorie
  const { categories } = useCategoryFetch();

  // Riferimenti per compatibilità con l'interfaccia
  const loaderRef = useRef<HTMLDivElement>(null);

  // Metadati della pagina
  const pageTitle = "Blog - Gildy";
  const pageDescription = "Leggi gli ultimi articoli su oro, argento, investimenti e mercati dei metalli preziosi.";
  const canonicalPath = "/blog";
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Blog Gildy",
    "description": pageDescription,
    "url": `https://gildy.it${canonicalPath}`
  };

  // Carica i post solo se non sono già disponibili e siamo nel browser
  useEffect(() => {
    if (typeof window !== 'undefined' && globalPosts.length === 0) {
      console.log('[ServerBlogPage] Caricamento dei post per SEO');
      setLoading(true);

      const loadPosts = async () => {
        try {
          const result = await getServerBlogPosts({
            page: 1,
            perPage: 30, // Aumentato da 20 a 30
            searchTerm: '',
            category: null,
            fetchAll: true // Attiva il caricamento di tutti i post per SEO
          });

          if (result.posts.length > 0) {
            console.log(`[ServerBlogPage] Caricati ${result.posts.length} post per SEO`);
            setPosts(result.posts);
            setTotalPostsLoaded(result.posts.length);
          } else {
            console.warn('[ServerBlogPage] Nessun post caricato per SEO');
          }
          setLoading(false);
        } catch (err) {
          console.error('[ServerBlogPage] Errore durante il caricamento dei post:', err);
          setLoading(false);
        }
      };

      loadPosts();
    } else if (globalPosts.length > 0) {
      console.log(`[ServerBlogPage] Utilizzando ${globalPosts.length} post precaricati`);
      setTotalPostsLoaded(globalPosts.length);
    }
  }, [globalPosts.length]);

  // Log per debug
  useEffect(() => {
    console.log(`[ServerBlogPage] Rendering con ${posts.length} post`);
  }, [posts.length]);

  // Utilizziamo direttamente globalPosts per garantire che i post siano disponibili durante il rendering iniziale
  const postsToRender = globalPosts.length > 0 ? globalPosts : posts;
  
  // Log per debug
  console.log(`[ServerBlogPage] postsToRender: ${postsToRender.length} posts`);

  return (
    <BlogLayout
      title={pageTitle}
      description={pageDescription}
      canonicalPath={canonicalPath}
      structuredData={structuredData}
      ssrMode={true}
    >
      <BlogHeader />

      <BlogFiltersSection
        searchInputValue=""
        onSearchChange={() => {}}
        categories={categories}
        selectedCategoryId={null}
        selectedCategoryName=""
        onCategoryClick={() => {}}
        onResetFilters={() => {}}
        isSearching={false}
      />

      {/* SEO monitoring info - hidden from users, visible to crawler */}
      <div className="sr-only">
        <p>Numero totale di articoli indicizzati per SEO: {totalPostsLoaded}</p>
      </div>

      <ServerBlogContentSection
        posts={postsToRender}
        loading={false} // Forziamo loading a false per garantire che i post vengano renderizzati
        loadingMore={false}
        hasMore={false}
        searchTerm=""
        selectedCategoryName=""
        selectedCategoryId={null}
        onResetFilters={() => {}}
        loaderRef={loaderRef}
        isCategoryChanging={false}
      />
    </BlogLayout>
  );
};

export default ServerBlogPage;
